import {
  Component,
  Output,
  EventEmitter,
  TemplateRef,
  ChangeDetectionStrategy,
  input,
  linkedSignal,
  computed,
  contentChild,
} from '@angular/core';
import { HumanizePipe } from '@app/core/pipes/humanize.pipe';
import { ClickedOutsideDirective } from '../../directives/clicked-outside.directive';
import { FormsModule } from '@angular/forms';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { NgTemplateOutlet } from '@angular/common';
import { Ag1LoaderComponent } from '../ag1-loader/ag1-loader.component';
import { SentenceCasePipe } from '../../pipes/sentence.pipe';

export interface SearchEvent {
  searchTerm: string;
  searchField: string;
}

export interface ISeachField {
  name: string;
  displayName?: string;
}
@Component({
  selector: 'ag1-search',
  templateUrl: './ag1-search.component.html',
  providers: [HumanizePipe],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    ClickedOutsideDirective,
    FormsModule,
    AngularSvgIconModule,
    NgTemplateOutlet,
    Ag1LoaderComponent,
    HumanizePipe,
    SentenceCasePipe,
  ],
})
export class Ag1SearchComponent {
  // (optional) allow users to search by specific fields
  // if not provided, search will be done on all fields
  // ui will not show field dropdown if not provided
  readonly searchFields = input<string[] | ISeachField[]>([]);
  readonly placeholder = input('');
  readonly disabled = input<boolean>(undefined);
  readonly btnVariant = input<
    'primary' | 'secondary' | 'tertiary' | 'success' | 'danger'
  >('primary');
  readonly showResults = input<boolean>(false);
  readonly isLoading = input<boolean>(false);
  /** Parent may seed one-way; local copy is what the UI / ngModel writes. */
  readonly searchField = input('all');
  readonly localSearchField = linkedSignal(() => this.searchField());
  readonly showAllFieldsOption = input(true);

  // passed in results template
  readonly customResultsRef = contentChild<TemplateRef<any>>('customResults');

  @Output()
  searchEvent = new EventEmitter<SearchEvent>();

  readonly searchTerm = input('');
  readonly localSearchTerm = linkedSignal(() => this.searchTerm());

  showResultsTemplate: boolean = false;

  readonly hasSearchFields = computed(() => this.searchFields()?.length > 0);

  isSimpleType(field: string | ISeachField): boolean {
    return typeof field === 'string';
  }

  hasDisplayName(field: string | ISeachField): boolean {
    if (this.isSimpleType(field)) {
      return false;
    }

    return (field as ISeachField).displayName !== undefined;
  }

  onFieldChangeEvent(event): void {
    // run search on field change
    if (!this.localSearchTerm()) {
      this.showResultsTemplate = false;
      return;
    }

    this.localSearchField.set(event);
    this.runSearch();
  }

  onEnterPressEvent(event: KeyboardEvent): void {
    // run search on enter key press
    if (event.key !== 'Enter') {
      return;
    }

    this.runSearch();
  }

  onModelChangeEvent(value: string): void {
    this.localSearchTerm.set(value);
    this.showResultsTemplate = false;

    // run search when clicking the clear button
    if (this.localSearchTerm() !== '') {
      return;
    }

    this.runSearch();
  }

  onSearchButtonEvent(): void {
    // run search on button click
    this.runSearch();
  }

  runSearch(): void {
    this.searchEvent.emit({
      searchField: this.localSearchField(),
      searchTerm: this.localSearchTerm().trim(),
    });
    this.showResultsTemplate = true;
  }
}
